import { Component, Watch } from 'vue-property-decorator';
import EditorBase from '../EditorBase';
import { Button } from 'streamlabs-beaker';
import Position from './Position.vue';
import GeneralSettings from './GeneralSettings.vue';
import VisibilityDeleteComponentButton from './VisibilityDeleteComponentButton.vue';
import { Selector, Slider, StatusSwitch } from 'streamlabs-beaker';
import { IMediaInputDevices, IWebcamComponent } from '@/helpers/editor';
import { cloneDeep } from 'lodash';

@Component({
  components: {
    SButton: Button,
    Position,
    GeneralSettings,
    VisibilityDeleteComponentButton,
    SSelector: Selector,
    SSlider: Slider,
    SStatusSwitch: StatusSwitch,
  },
})
export default class WebcamSettings extends EditorBase {
  get hasDeviceId(): boolean {
    if (!this.videoInputDevices) return false;
    return this.videoInputDevices.every(device => device.deviceId);
  }

  get hasAudioDeviceId(): boolean {
    if (!this.audioInputDevices) return false;
    return this.audioInputDevices.every(device => device.deviceId);
  }

  get hasFrontOrBackCamera(): boolean {
    // iPad device id is inconsistent
    return this.videoInputDevices.some(device => device.label === 'Front Camera') && this.videoInputDevices.some(device => device.label === 'Back Camera');
  }

  get webcamDropdownOptions() {
    if (this.isMobile || !this.hasDeviceId || this.hasFrontOrBackCamera) return [
      {
        label: 'Front Camera',
        value: 'user',
      },
      {
        label: 'Back Camera',
        value: 'environment',
      }
    ];

    return this.videoInputDevices?.map((device: IMediaInputDevices) => {
      return {
        label: device.label,
        value: device,
      };
    });
  }

  get audioDropdownOptions() {
    if (!this.hasAudioDeviceId) return [];

    return this.audioInputDevices?.map((device: IMediaInputDevices) => {
      return {
        label: device.label,
        value: device,
      };
    });
  }

  get audioDropdownValue() {
    if (!this.selectedAudioInputDevice) {
      if (!this.audioInputDevices) return null;

      return {
        label: this.audioInputDevices[0].label,
        value: this.audioInputDevices[0],
      };
    }

    return { label: this.selectedAudioInputDevice.label, value: this.selectedAudioInputDevice };
  }

  get dropdownValue() {
    if (this.isMobile || !this.hasDeviceId || this.hasFrontOrBackCamera) return {
      label: this.selectedFacingMode === 'user' ? 'Front Camera' : 'Back Camera',
      value: this.selectedFacingMode,
    };

    if (!this.selectedVideoInputDevice) {
      return {
        label: this.videoInputDevices[0].label,
        value: this.videoInputDevices[0],
      };
    }

    return { label: this.selectedVideoInputDevice.label, value: this.selectedVideoInputDevice };
  }

  get delay(): number {
    return (this.selectedComponent as IWebcamComponent)?.delay ? (this.selectedComponent as IWebcamComponent).delay : 0;
  }

  get volume(): number {
    return (this.selectedComponent as IWebcamComponent)?.volume || (this.selectedComponent as IWebcamComponent)?.volume === 0 ?
      (this.selectedComponent as IWebcamComponent).volume :
      100;
  }

  get muted(): boolean {
    return (this.selectedComponent as IWebcamComponent)?.muted;
  }

  setWebcamSource(value) {
    const webcamComponent: IWebcamComponent = cloneDeep(this.selectedComponent as IWebcamComponent);
    if (this.isMobile || !this.hasDeviceId || this.hasFrontOrBackCamera) {
      this.selectedFacingMode = value.value;
      webcamComponent.facingMode = value.value;
    } else {
      this.selectedVideoInputDevice = value.value;
      webcamComponent.videoDeviceId = value.value.deviceId;
    }

    this.onComponentUpdate(webcamComponent);
  }

  setAudioSource(value) {
    const webcamComponent: IWebcamComponent = cloneDeep(this.selectedComponent as IWebcamComponent);
    this.selectedAudioInputDevice = value.value;
    webcamComponent.audioDeviceId = value.value.deviceId;

    this.onComponentUpdate(webcamComponent);
  }

  onDelayUpdate(value) {
    const webcamComponent: IWebcamComponent = cloneDeep(this.selectedComponent as IWebcamComponent);

    webcamComponent.delay = value;
    this.onComponentUpdate(webcamComponent);
  }

  onVolumeUpdate(value) {
    const webcamComponent: IWebcamComponent = cloneDeep(this.selectedComponent as IWebcamComponent);

    webcamComponent.volume = value;
    this.onComponentUpdate(webcamComponent);
  }

  toggleMuted() {
    const webcamComponent: IWebcamComponent = cloneDeep(this.selectedComponent as IWebcamComponent);

    webcamComponent.muted = !webcamComponent.muted;
    this.onComponentUpdate(webcamComponent);
  }
}
